import React, { useState } from "react";
import { History, ChevronDown, ChevronRight, User } from "lucide-react";
import Pagination from "../../../components/common/Pagination";

const ACTION_STYLES = {
  create: "bg-emerald-100 text-emerald-800 border-emerald-200",
  update: "bg-blue-100 text-blue-800 border-blue-200",
  delete: "bg-red-100 text-red-800 border-red-200",
};

const ACTION_LABELS = {
  create: "Thêm mới",
  update: "Cập nhật",
  delete: "Xóa",
};

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function LogRow({ row }) {
  const [open, setOpen] = useState(false);
  const changes = Array.isArray(row.changes) ? row.changes : [];
  const hasChanges = changes.length > 0;

  return (
    <div className="border-b border-gray-50 last:border-0">
      <div className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50/80">
        <div className="pt-0.5 shrink-0">
          {hasChanges ? (
            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="text-gray-400 hover:text-gray-600"
            >
              {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          ) : (
            <span className="w-4 inline-block" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span
              className={`text-[10px] font-black uppercase px-2 py-0.5 rounded border ${ACTION_STYLES[row.action] || "bg-gray-100"}`}
            >
              {ACTION_LABELS[row.action] || row.action}
            </span>
            <span className="text-xs text-gray-400">{formatWhen(row.created_at)}</span>
          </div>
          <p className="text-sm font-medium text-gray-800">{row.summary || row.usage_role_label}</p>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <User size={11} />
              {row.actor_name || row.actor_email || "Hệ thống"}
            </span>
            {row.branch_name && <span>CN: {row.branch_name}</span>}
            {row.department_name && <span>BP: {row.department_name}</span>}
          </div>
        </div>
      </div>
      {open && hasChanges && (
        <div className="px-4 pb-3 pl-11">
          <ul className="text-xs space-y-1 bg-gray-50 rounded-xl p-3 border border-gray-100">
            {changes.map((c, i) => (
              <li key={i} className="text-gray-700">
                <span className="font-bold">{c.label || c.field}:</span>{" "}
                <span className="text-gray-500">{c.from}</span>
                <span className="mx-1">→</span>
                <span className="text-gray-800">{c.to}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function UsageRoleLogsPanel({
  logs,
  total,
  page,
  pageSize,
  actionFilter,
  loading,
  onPageChange,
  onFilterChange,
}) {
  return (
    <div className="bg-white border rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <History size={18} className="text-red-600" />
          <div>
            <h2 className="text-sm font-bold text-gray-800">Nhật ký thay đổi</h2>
            <p className="text-xs text-gray-500">Lịch sử thêm mới và cập nhật quyền sử dụng</p>
          </div>
        </div>
        <div className="flex gap-1 flex-wrap">
          {[
            { value: "", label: "Tất cả" },
            { value: "create", label: "Thêm mới" },
            { value: "update", label: "Cập nhật" },
            { value: "delete", label: "Xóa" },
          ].map((opt) => (
            <button
              key={opt.value || "all"}
              type="button"
              onClick={() => onFilterChange(opt.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                actionFilter === opt.value
                  ? "bg-red-600 text-white border-red-600"
                  : "bg-white text-gray-600 border-gray-200 hover:border-red-200"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !logs?.length ? (
        <p className="text-center text-gray-400 py-10 text-sm">Đang tải nhật ký...</p>
      ) : !logs?.length ? (
        <p className="text-center text-gray-400 py-10 text-sm">
          Chưa có nhật ký. Thao tác thêm/sửa quyền sẽ hiển thị tại đây.
        </p>
      ) : (
        <div>{logs.map((row) => (
          <LogRow key={row.id} row={row} />
        ))}</div>
      )}

      {total > pageSize && (
        <div className="px-4 py-3 border-t">
          <Pagination
            currentPage={page}
            totalRecords={total}
            pageSize={pageSize}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
}
